"use client";

import { c as _c } from "react/compiler-runtime";
import { type Column as TanstackColumn, type ColumnDef as TanstackColumnDef, columnFacetingFeature, columnFilteringFeature, columnSizingFeature, columnVisibilityFeature, createFacetedUniqueValues, createFilteredRowModel, createSortedRowModel, filterFn_arrIncludesSome, filterFn_includesString, flexRender, type ColumnVisibilityState, type OnChangeFn, type RowData, rowSortingFeature, sortFn_alphanumeric, sortFn_basic, sortFn_datetime, sortFn_text, type SortingState, type Table as TanstackTable, tableFeatures, useTable } from "@tanstack/react-table";
import { twJoin } from "cn";
import { ChevronDownIcon, PlusCircleIcon } from "lucide-react";
import { type FC, useState } from "react";
import { TableBodyRowEmpty } from "../table-body-row-empty";
import { Badge } from "./badge";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow } from "./table";
const dataTableFeatures = tableFeatures({
  rowSortingFeature,
  columnFilteringFeature,
  columnVisibilityFeature,
  columnSizingFeature,
  columnFacetingFeature,
  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  facetedUniqueValues: createFacetedUniqueValues(),
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    basic: sortFn_basic,
    datetime: sortFn_datetime,
    text: sortFn_text
  },
  filterFns: {
    arrIncludesSome: filterFn_arrIncludesSome,
    includesString: filterFn_includesString
  }
});
type DataTableFeatures = typeof dataTableFeatures;
export type ColumnDef<TData extends RowData, TValue = unknown> = TanstackColumnDef<DataTableFeatures, TData, TValue>;
type Column<TData extends RowData, TValue = unknown> = TanstackColumn<DataTableFeatures, TData, TValue>;
type TableType<TData extends RowData> = TanstackTable<DataTableFeatures, TData>;
const emptyObject = {};
function getRecordId(record: unknown): string | undefined {
  if (!record || typeof record !== "object") {
    return undefined;
  }
  if ("_id" in record && typeof record._id === "string") {
    return record._id;
  }
  if ("id" in record && typeof record.id === "string") {
    return record.id;
  }
  return undefined;
}
function isInteractiveTableTarget(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest("a, button, input, textarea, select, [role='button']") !== null;
}
export function DataTable(t0) {
  const $ = _c(34);
  const {
    columns,
    data,
    toolbar: Toolbar,
    columnVisibility,
    setColumnVisibility,
    toolbarProps: t1,
    defaultSorting,
    id,
    onRowClick,
    isLoading,
    loadingUI: LoadingUI
  } = t0;
  const toolbarProps = t1 === undefined ? emptyObject as ToolbarProps : t1;
  let t2;
  if ($[0] !== defaultSorting) {
    t2 = defaultSorting ?? [];
    $[0] = defaultSorting;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [sorting, setSorting] = useState(t2);
  let t3;
  if ($[2] !== columnVisibility || $[3] !== sorting) {
    t3 = {
      columnVisibility,
      sorting
    };
    $[2] = columnVisibility;
    $[3] = sorting;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== columns || $[6] !== data || $[7] !== setColumnVisibility || $[8] !== t3) {
    t4 = {
      features: dataTableFeatures,
      data,
      columns,
      state: t3,
      onColumnVisibilityChange: setColumnVisibility,
      onSortingChange: setSorting
    };
    $[5] = columns;
    $[6] = data;
    $[7] = setColumnVisibility;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const table = useTable(t4);
  let t5;
  if ($[10] !== Toolbar || $[11] !== table || $[12] !== toolbarProps) {
    t5 = Toolbar && <Toolbar table={table} {...toolbarProps} />;
    $[10] = Toolbar;
    $[11] = table;
    $[12] = toolbarProps;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const t6 = `${id}-table`;
  let t7;
  if ($[14] !== table) {
    t7 = table.getHeaderGroups().map(_temp2);
    $[14] = table;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t7) {
    t8 = <TableHeader>{t7}</TableHeader>;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== LoadingUI || $[19] !== columns || $[20] !== isLoading || $[21] !== onRowClick || $[22] !== table) {
    t9 = isLoading && LoadingUI ? <LoadingUI /> : table.getRowModel().rows.length ? table.getRowModel().rows.map(row => {
      const recordId = getRecordId(row.original) ?? row.id;
      const handleActivateRow = () => {
        onRowClick?.(recordId);
      };
      return <TableRow className={onRowClick ? "cursor-pointer" : undefined} key={row.id} onClick={onRowClick ? event => {
        if (isInteractiveTableTarget(event.target)) {
          return;
        }
        handleActivateRow();
      } : undefined} onKeyDown={onRowClick ? event_0 => {
        if (event_0.key !== "Enter" && event_0.key !== " ") {
          return;
        }
        if (isInteractiveTableTarget(event_0.target)) {
          return;
        }
        event_0.preventDefault();
        handleActivateRow();
      } : undefined} tabIndex={onRowClick ? 0 : undefined}>{row.getVisibleCells().map(_temp3)}</TableRow>;
    }) : <TableBodyRowEmpty columns={columns.length} message="Nenhum resultado" />;
    $[18] = LoadingUI;
    $[19] = columns;
    $[20] = isLoading;
    $[21] = onRowClick;
    $[22] = table;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t9) {
    t10 = <TableBody>{t9}</TableBody>;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t6 || $[28] !== t8) {
    t11 = <TableContainer><Table id={t6}>{t8}{t10}</Table></TableContainer>;
    $[26] = t10;
    $[27] = t6;
    $[28] = t8;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== id || $[31] !== t11 || $[32] !== t5) {
    t12 = <div className="flex flex-col gap-3" id={id}>{t5}{t11}</div>;
    $[30] = id;
    $[31] = t11;
    $[32] = t5;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}
function _temp3(cell) {
  return <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>;
}
function _temp2(headerGroup) {
  return <TableRow key={headerGroup.id}>{headerGroup.headers.map(_temp)}</TableRow>;
}
function _temp(header) {
  const headerContent = <>{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}{header.column.getIsSorted() && <div className="bg-muted grid size-5 place-items-center rounded-sm"><ChevronDownIcon className={twJoin("text-foreground size-4 transition-transform", header.column.getIsSorted() === "desc" && "rotate-180")} /></div>}</>;
  return <TableHead key={header.id}>{header.column.getCanSort() ? <button className="flex w-full items-center gap-2" onClick={() => header.column.toggleSorting()} type="button">{headerContent}</button> : <div className="flex w-full items-center gap-2">{headerContent}</div>}</TableHead>;
}
export function DataTableFacetedFilter(t0) {
  const $ = _c(48);
  const {
    column,
    title,
    options,
    placeholder: t1
  } = t0;
  const placeholder = t1 === undefined ? "Buscar..." : t1;
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let selectedValues;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== column || $[1] !== options || $[2] !== placeholder || $[3] !== title) {
    const facets = column?.getFacetedUniqueValues();
    selectedValues = new Set(column?.getFilterValue() as string[]);
    T4 = Popover;
    let t8;
    let t9;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <Button className="border-border focus-visible:border-ring rounded-xl border-dashed transition-none focus-visible:border focus-visible:ring-0 active:scale-100" size="sm" variant="outline" />;
      t9 = <PlusCircleIcon className="size-4" />;
      $[16] = t8;
      $[17] = t9;
    } else {
      t8 = $[16];
      t9 = $[17];
    }
    const t10 = selectedValues.size > 0 && <><Badge className="rounded-md px-1 font-normal lg:hidden" variant="secondary">{selectedValues.size}</Badge><div className="hidden space-x-1 lg:flex">{selectedValues.size > 2 ? <Badge className="px-1 font-normal" variant="secondary">{selectedValues.size} selecionados</Badge> : options.filter(option => selectedValues.has(option.value)).map(_temp4)}</div></>;
    if ($[18] !== t10 || $[19] !== title) {
      t7 = <PopoverTrigger render={t8}>{t9}{title}{t10}</PopoverTrigger>;
      $[18] = t10;
      $[19] = title;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    T3 = PopoverContent;
    t5 = "start";
    t6 = "w-60 p-0";
    T2 = Command;
    if ($[21] !== placeholder) {
      t4 = <CommandInput placeholder={placeholder} />;
      $[21] = placeholder;
      $[22] = t4;
    } else {
      t4 = $[22];
    }
    T1 = CommandList;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>;
      $[23] = t3;
    } else {
      t3 = $[23];
    }
    T0 = CommandGroup;
    t2 = options.map(option_1 => {
      const isSelected = selectedValues.has(option_1.value);
      return <CommandItem className="flex items-center gap-2" key={option_1.value} onSelect={() => {
        if (isSelected) {
          selectedValues.delete(option_1.value);
        } else {
          selectedValues.add(option_1.value);
        }
        const filterValues = Array.from(selectedValues);
        column?.setFilterValue(filterValues.length ? filterValues : undefined);
      }} value={option_1.value}><Checkbox checked={isSelected} className="pointer-events-none" /><span>{option_1.label}</span>{facets?.get(option_1.value) && <span className="ml-auto flex size-4 items-center justify-center font-mono text-xs">{facets.get(option_1.value)}</span>}</CommandItem>;
    });
    $[0] = column;
    $[1] = options;
    $[2] = placeholder;
    $[3] = title;
    $[4] = T0;
    $[5] = T1;
    $[6] = T2;
    $[7] = T3;
    $[8] = T4;
    $[9] = selectedValues;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    T0 = $[4];
    T1 = $[5];
    T2 = $[6];
    T3 = $[7];
    T4 = $[8];
    selectedValues = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
    t7 = $[15];
  }
  let t8;
  if ($[24] !== T0 || $[25] !== t2) {
    t8 = <T0>{t2}</T0>;
    $[24] = T0;
    $[25] = t2;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== column || $[28] !== selectedValues.size) {
    t9 = selectedValues.size > 0 && <><CommandSeparator /><CommandGroup><CommandItem className="justify-center text-center" onSelect={() => column?.setFilterValue(undefined)}>Limpar filtros</CommandItem></CommandGroup></>;
    $[27] = column;
    $[28] = selectedValues.size;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== T1 || $[31] !== t3 || $[32] !== t8 || $[33] !== t9) {
    t10 = <T1>{t3}{t8}{t9}</T1>;
    $[30] = T1;
    $[31] = t3;
    $[32] = t8;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== T2 || $[36] !== t10 || $[37] !== t4) {
    t11 = <T2>{t4}{t10}</T2>;
    $[35] = T2;
    $[36] = t10;
    $[37] = t4;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== T3 || $[40] !== t11 || $[41] !== t5 || $[42] !== t6) {
    t12 = <T3 align={t5} className={t6}>{t11}</T3>;
    $[39] = T3;
    $[40] = t11;
    $[41] = t5;
    $[42] = t6;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  let t13;
  if ($[44] !== T4 || $[45] !== t12 || $[46] !== t7) {
    t13 = <T4>{t7}{t12}</T4>;
    $[44] = T4;
    $[45] = t12;
    $[46] = t7;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  return t13;
}
function _temp4(option_0) {
  return <Badge className="rounded-md px-1 font-normal" key={option_0.value} variant="secondary">{option_0.label}</Badge>;
}
export function DataTableFilter(t0) {
  const $ = _c(55);
  const {
    title,
    options,
    placeholder: t1,
    values,
    onChange
  } = t0;
  const placeholder = t1 === undefined ? "Buscar..." : t1;
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let selectedValues;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== onChange || $[1] !== options || $[2] !== placeholder || $[3] !== title || $[4] !== values) {
    selectedValues = new Set(values);
    const selectedSummary = selectedValues.size === 1 ? options.find(option => selectedValues.has(option.value))?.label ?? values[0] : selectedValues.size > 1 ? String(selectedValues.size) : null;
    T4 = Popover;
    const t8 = selectedSummary ? "border-foreground/60" : "text-foreground";
    let t9;
    if ($[17] !== t8) {
      t9 = twJoin("font-medium", t8);
      $[17] = t8;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    let t10;
    if ($[19] !== t9) {
      t10 = <Button className={t9} variant="outline" />;
      $[19] = t9;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    let t11;
    if ($[21] !== selectedSummary) {
      t11 = selectedSummary ? <>:<span className="max-w-40 truncate font-semibold">{selectedSummary}</span></> : null;
      $[21] = selectedSummary;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    let t12;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <ChevronDownIcon className="text-muted-foreground" data-icon="inline-end" />;
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    if ($[24] !== t10 || $[25] !== t11 || $[26] !== title) {
      t7 = <PopoverTrigger render={t10}>{title}{t11}{t12}</PopoverTrigger>;
      $[24] = t10;
      $[25] = t11;
      $[26] = title;
      $[27] = t7;
    } else {
      t7 = $[27];
    }
    T3 = PopoverContent;
    t5 = "start";
    t6 = "w-60 p-0";
    T2 = Command;
    if ($[28] !== placeholder) {
      t4 = <CommandInput placeholder={placeholder} />;
      $[28] = placeholder;
      $[29] = t4;
    } else {
      t4 = $[29];
    }
    T1 = CommandList;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>;
      $[30] = t3;
    } else {
      t3 = $[30];
    }
    T0 = CommandGroup;
    t2 = options.map(option_0 => {
      const isSelected = selectedValues.has(option_0.value);
      return <CommandItem className={`flex items-center gap-2 ${isSelected ? "bg-accent/50 text-accent-foreground data-[selected=true]:bg-accent" : "hover:bg-accent/30"}`} key={option_0.value} onSelect={() => {
        if (isSelected) {
          selectedValues.delete(option_0.value);
        } else {
          selectedValues.add(option_0.value);
        }
        const filterValues = Array.from(selectedValues);
        onChange(filterValues);
      }} value={option_0.label}><Checkbox checked={isSelected} className="border-input *:text-primary-foreground data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground pointer-events-none size-4 border-2" /><span className={isSelected ? "font-medium" : "font-normal"}>{option_0.label}</span></CommandItem>;
    });
    $[0] = onChange;
    $[1] = options;
    $[2] = placeholder;
    $[3] = title;
    $[4] = values;
    $[5] = T0;
    $[6] = T1;
    $[7] = T2;
    $[8] = T3;
    $[9] = T4;
    $[10] = selectedValues;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    T0 = $[5];
    T1 = $[6];
    T2 = $[7];
    T3 = $[8];
    T4 = $[9];
    selectedValues = $[10];
    t2 = $[11];
    t3 = $[12];
    t4 = $[13];
    t5 = $[14];
    t6 = $[15];
    t7 = $[16];
  }
  let t8;
  if ($[31] !== T0 || $[32] !== t2) {
    t8 = <T0>{t2}</T0>;
    $[31] = T0;
    $[32] = t2;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  let t9;
  if ($[34] !== onChange || $[35] !== selectedValues.size) {
    t9 = selectedValues.size > 0 && <><CommandSeparator /><CommandGroup><CommandItem className="justify-center text-center" onSelect={() => onChange([])}>Limpar</CommandItem></CommandGroup></>;
    $[34] = onChange;
    $[35] = selectedValues.size;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== T1 || $[38] !== t3 || $[39] !== t8 || $[40] !== t9) {
    t10 = <T1>{t3}{t8}{t9}</T1>;
    $[37] = T1;
    $[38] = t3;
    $[39] = t8;
    $[40] = t9;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  let t11;
  if ($[42] !== T2 || $[43] !== t10 || $[44] !== t4) {
    t11 = <T2>{t4}{t10}</T2>;
    $[42] = T2;
    $[43] = t10;
    $[44] = t4;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== T3 || $[47] !== t11 || $[48] !== t5 || $[49] !== t6) {
    t12 = <T3 align={t5} className={t6}>{t11}</T3>;
    $[46] = T3;
    $[47] = t11;
    $[48] = t5;
    $[49] = t6;
    $[50] = t12;
  } else {
    t12 = $[50];
  }
  let t13;
  if ($[51] !== T4 || $[52] !== t12 || $[53] !== t7) {
    t13 = <T4>{t7}{t12}</T4>;
    $[51] = T4;
    $[52] = t12;
    $[53] = t7;
    $[54] = t13;
  } else {
    t13 = $[54];
  }
  return t13;
}